import React, { useState } from "react";
import { Badge, Button, DatePicker, Input, Popover, Select, Space, Tag, theme } from "antd";
import { FilterOutlined, ReloadOutlined } from "@ant-design/icons";
import { TABLE_TOOLBAR_HEIGHT } from "./constants";

export interface ToolbarAction {
  key: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  type?: "primary" | "default" | "danger";
  onClick: () => void;
  disabled?: boolean;
}

export interface FilterCriteria {
  key: string;
  label: string;
  type: "select" | "dateRange" | "text" | "multiSelect";
  options?: { label: string; value: any }[];
  placeholder?: string;
  showSearch?: boolean;
}

const defaultFilterOption = (input: string, option?: { label?: any; value?: any }) => {
  if (!input) return true;
  const q = input.toLowerCase().trim();
  const labelStr = typeof option?.label === "string" ? option.label : String(option?.label ?? "");
  const valueStr = typeof option?.value === "string" ? option.value : String(option?.value ?? "");
  return labelStr.toLowerCase().includes(q) || valueStr.toLowerCase().includes(q);
};

interface TableToolbarProps {
  onSearch?: (value: string) => void;
  searchPlaceholder?: string;
  filterCriteria?: FilterCriteria[];
  activeFilters?: Record<string, any>;
  onFiltersChange?: (filters: Record<string, any>) => void;
  onRefresh?: () => void;
  /** Extra nodes rendered before active filter tags (e.g. DateRangePicker) */
  extraBeforeFilters?: React.ReactNode;
  /** Extra nodes rendered after add filter button (e.g. ColumnSelector) */
  extra?: React.ReactNode;
  primaryActions?: ToolbarAction[];
}

const ActiveFilterTag: React.FC<{
  filterKey: string;
  value: any;
  criterion?: FilterCriteria;
  onUpdate: (key: string, newValue: any) => void;
  onRemove: (key: string) => void;
  getTagLabel: (key: string, value: any) => string;
  token: any;
}> = ({ filterKey, value, criterion, onUpdate, onRemove, getTagLabel, token }) => {
  const [open, setOpen] = useState(false);
  const [editVal, setEditVal] = useState<any>(value);

  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setEditVal(value);
    }
    setOpen(newOpen);
  };

  const handleApply = () => {
    if (editVal != null) {
      onUpdate(filterKey, editVal);
      setOpen(false);
    }
  };

  const popoverContent = criterion ? (
    <div style={{ width: 220, padding: "2px 0" }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: token.colorTextSecondary, marginBottom: 6 }}>
        Edit {criterion.label}
      </div>
      {criterion.type === "select" && (
        <Select
          showSearch={criterion.showSearch ?? true}
          optionFilterProp="label"
          filterOption={defaultFilterOption}
          style={{ width: "100%", marginBottom: 10 }}
          placeholder={criterion.placeholder}
          value={editVal}
          onChange={setEditVal}
          options={criterion.options}
        />
      )}
      {criterion.type === "multiSelect" && (
        <Select
          showSearch={criterion.showSearch ?? true}
          optionFilterProp="label"
          filterOption={defaultFilterOption}
          mode="multiple"
          maxTagCount="responsive"
          style={{ width: "100%", marginBottom: 10 }}
          placeholder={criterion.placeholder}
          value={editVal ?? []}
          onChange={setEditVal}
          options={criterion.options}
        />
      )}
      {criterion.type === "dateRange" && (
        <DatePicker.RangePicker
          style={{ width: "100%", marginBottom: 10 }}
          format="DD/MM/YYYY"
          value={editVal}
          onChange={setEditVal}
        />
      )}
      {criterion.type === "text" && (
        <Input
          style={{ marginBottom: 10 }}
          placeholder={criterion.placeholder ?? "Type value..."}
          value={editVal ?? ""}
          onChange={(e) => setEditVal(e.target.value)}
        />
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
        <Button size="small" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={handleApply}
          disabled={editVal == null || editVal === ""}
        >
          Update
        </Button>
      </div>
    </div>
  ) : null;

  const tagLabel = getTagLabel(filterKey, value);
  const tagElement = (
    <Tag
      closable
      onClose={(e) => {
        e.stopPropagation();
        onRemove(filterKey);
      }}
      className="table-toolbar-filter-tag"
      style={{ cursor: criterion ? "pointer" : "default" }}
      title={tagLabel}
    >
      <span className="filter-tag-text">
        {tagLabel}
      </span>
    </Tag>
  );

  if (!criterion) return tagElement;

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      placement="bottom"
    >
      {tagElement}
    </Popover>
  );
};

const TableToolbar: React.FC<TableToolbarProps> = ({
  onSearch,
  searchPlaceholder = "Search...",
  filterCriteria,
  activeFilters,
  onFiltersChange,
  onRefresh,
  extraBeforeFilters,
  extra,
  primaryActions = [],
}) => {
  const { token } = theme.useToken();
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string>("");
  const [criteriaValue, setCriteriaValue] = useState<any>(undefined);

  const activeKeys = activeFilters
    ? Object.keys(activeFilters).filter(
      (k) =>
        activeFilters[k] !== undefined &&
        activeFilters[k] !== null &&
        (Array.isArray(activeFilters[k]) ? activeFilters[k].length > 0 : activeFilters[k] !== "")
    )
    : [];

  const handleApply = () => {
    if (!selectedKey || criteriaValue == null) return;
    onFiltersChange?.({ ...activeFilters, [selectedKey]: criteriaValue });
    setFilterOpen(false);
    setSelectedKey("");
    setCriteriaValue(undefined);
  };

  const handleRemove = (key: string) => {
    if (!onFiltersChange || !activeFilters) return;
    const copy = { ...activeFilters };
    delete copy[key];
    onFiltersChange(copy);
  };

  const getTagLabel = (key: string, value: any) => {
    const c = filterCriteria?.find((x) => x.key === key);
    if (!c) return `${key}: ${String(value)}`;
    if (c.type === "dateRange" && Array.isArray(value) && value.length === 2) {
      const fmt = (v: any) => (typeof v?.format === "function" ? v.format("DD/MM/YYYY") : String(v));
      return `${c.label}: ${fmt(value[0])} – ${fmt(value[1])}`;
    }
    if (c.type === "select" || c.type === "multiSelect") {
      const vals = Array.isArray(value) ? value : [value];
      const labels = vals.map((v: any) => c.options?.find((o) => o.value === v)?.label ?? String(v));
      return `${c.label}: ${labels.join(", ")}`;
    }
    return `${c.label}: ${String(value)}`;
  };

  const selectedCriterion = filterCriteria?.find((c) => c.key === selectedKey);

  const filterPopover = (
    <div style={{ width: 220 }}>
      <div style={{ fontSize: 11, fontWeight: 500, color: token.colorTextSecondary, marginBottom: 4 }}>
        Add filter
      </div>
      <Select
        showSearch
        optionFilterProp="label"
        filterOption={defaultFilterOption}
        style={{ width: "100%", marginBottom: 10 }}
        placeholder="Select filter"
        value={selectedKey || undefined}
        onChange={(v) => { setSelectedKey(v); setCriteriaValue(undefined); }}
        options={filterCriteria?.map((c) => ({ value: c.key, label: c.label }))}
      />
      {selectedCriterion && (
        <>
          <div style={{ fontSize: 11, fontWeight: 500, color: token.colorTextSecondary, marginBottom: 4 }}>
            Value
          </div>
          {selectedCriterion.type === "select" && (
            <Select
              showSearch={selectedCriterion.showSearch ?? true}
              optionFilterProp="label"
              filterOption={defaultFilterOption}
              style={{ width: "100%", marginBottom: 10 }}
              placeholder={selectedCriterion.placeholder}
              value={criteriaValue}
              onChange={setCriteriaValue}
              options={selectedCriterion.options}
            />
          )}
          {selectedCriterion.type === "multiSelect" && (
            <Select
              showSearch={selectedCriterion.showSearch ?? true}
              optionFilterProp="label"
              filterOption={defaultFilterOption}
              mode="multiple"
              maxTagCount="responsive"
              style={{ width: "100%", marginBottom: 10 }}
              placeholder={selectedCriterion.placeholder}
              value={criteriaValue ?? []}
              onChange={setCriteriaValue}
              options={selectedCriterion.options}
            />
          )}
          {selectedCriterion.type === "dateRange" && (
            <DatePicker.RangePicker
              style={{ width: "100%", marginBottom: 10 }}
              format="DD/MM/YYYY"
              value={criteriaValue}
              onChange={setCriteriaValue}
            />
          )}
          {selectedCriterion.type === "text" && (
            <Input
              style={{ marginBottom: 10 }}
              placeholder={selectedCriterion.placeholder ?? "Type value..."}
              value={criteriaValue ?? ""}
              onChange={(e) => setCriteriaValue(e.target.value)}
            />
          )}
        </>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
        <Button size="small" onClick={() => { setFilterOpen(false); setSelectedKey(""); setCriteriaValue(undefined); }}>
          Cancel
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={handleApply}
          disabled={!selectedKey || criteriaValue == null}
        >
          Apply
        </Button>
      </div>
    </div>
  );

  return (
    <div className="aoa-table-toolbar">
      <style>{`
        .aoa-table-toolbar {
          min-height: ${TABLE_TOOLBAR_HEIGHT}px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 8px 12px;
          padding: 8px 0;
          width: 100%;
        }
        .aoa-table-toolbar-left {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
        }
        .aoa-table-toolbar-right {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1 1 auto;
          justify-content: flex-end;
          flex-wrap: wrap;
        }
        .aoa-toolbar-search {
          flex: 1 1 130px;
          max-width: 190px;
          min-width: 120px;
        }
        .table-toolbar-filter-tag {
          background-color: ${token.colorPrimaryBg} !important;
          border: 1px solid ${token.colorPrimaryBorder} !important;
          color: ${token.colorPrimary} !important;
          border-radius: 6px !important;
          font-size: 12px !important;
          font-weight: 500 !important;
          padding: 4px 9px !important;
          display: inline-flex !important;
          align-items: center !important;
          margin: 0 !important;
          gap: 5px !important;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02) !important;
          height: 32px !important;
          line-height: normal !important;
          white-space: nowrap !important;
        }
        .filter-tag-text {
          max-width: 250px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          display: inline-block;
          vertical-align: middle;
        }
        .table-toolbar-filter-tag:hover {
          background-color: ${token.colorPrimaryBg} !important;
          border-color: ${token.colorPrimaryHover} !important;
          transform: translateY(-0.5px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
        }
        .table-toolbar-filter-tag .ant-tag-close-icon {
          color: ${token.colorPrimary} !important;
          font-size: 10px !important;
          transition: all 0.15s ease !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          margin-left: 2px !important;
        }
        .table-toolbar-filter-tag .ant-tag-close-icon:hover {
          color: ${token.colorError} !important;
          transform: scale(1.1);
        }
      `}</style>

      {/* Left: refresh + primary actions */}
      <div className="aoa-table-toolbar-left">
        {primaryActions.map((action) => (
          <Button
            key={action.key}
            type={action.type === "primary" ? "primary" : action.type === "danger" ? "default" : "default"}
            danger={action.type === "danger"}
            icon={action.icon}
            onClick={action.onClick}
            disabled={action.disabled}
          >
            {action.label}
          </Button>
        ))}
        {onRefresh && (
          <Button icon={<ReloadOutlined />} onClick={onRefresh} title="Refresh" />
        )}
      </div>

      {/* Right: search + date range + selected values + add filter + extra */}
      <div className="aoa-table-toolbar-right">
        {onSearch && (
          <div className="aoa-toolbar-search">
            <Input.Search
              allowClear
              placeholder={searchPlaceholder}
              onSearch={(e) => onSearch(e)}
              onClear={() => onSearch("")}
              onChange={(e) => {
                if (!e.target.value) {
                  onSearch("");
                }
              }}
              style={{ width: "100%" }}
              size="middle"
            />
          </div>
        )}

        {/* 1. Date Range / extraBeforeFilters */}
        {extraBeforeFilters}

        {/* 2. Selected Value (Active filter tags) */}
        {activeKeys.map((key) => (
          <ActiveFilterTag
            key={key}
            filterKey={key}
            value={activeFilters![key]}
            criterion={filterCriteria?.find((c) => c.key === key)}
            onUpdate={(k, v) => onFiltersChange?.({ ...activeFilters, [k]: v })}
            onRemove={handleRemove}
            getTagLabel={getTagLabel}
            token={token}
          />
        ))}

        {/* 3. Add Filter button (Icon button) */}
        {filterCriteria && filterCriteria.length > 0 && (
          <Popover
            content={filterPopover}
            trigger="click"
            open={filterOpen}
            onOpenChange={setFilterOpen}
            placement="bottomRight"
          >
            <Badge count={activeKeys.length} size="small" offset={[-2, 2]}>
              <Button
                icon={<FilterOutlined />}
                title="Add filter"
                style={{ color: activeKeys.length > 0 ? token.colorPrimary : undefined }}
              />
            </Badge>
          </Popover>
        )}

        {/* 4. Column selector / extra */}
        {extra}
      </div>
    </div>
  );
};

export default TableToolbar;
